<template>
  <div ref="scrollRef">
    <div
      ref="realBoxRef"
      @mouseenter="enter"
      @mouseleave="leave"
      :class="cssStep ? 'step realBox' : 'realBox'"
    >
      <div ref="slotListRef" :style="floatStyle">
        <slot></slot>
      </div>
      <div v-if="slotHtml && showHtml" :style="floatStyle">
        <slot name="html" />
      </div>
      <div
        v-else-if="!slotHtml && showHtml"
        v-html="scrollHtml"
        :style="floatStyle"
      ></div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  nextTick,
  watch,
} from "vue";

import { dataWarm, validator } from "../../public";

export default defineComponent({
  name: "css-seamless-scroll",
  props: {
    datas: {
      type: Array,
      required: true,
    },
    // 步进速度，step 需是单步大小的约数
    step: {
      type: [Number, String],
      validator,
    },
    // 开启滚动的数据量
    limitScrollNum: {
      type: [Number, String],
      default: 5,
      validator,
    },
    // 是否开启鼠标悬停
    hover: {
      type: Boolean,
      default: false,
    },
    // 控制滚动方向
    direction: {
      type: String,
      default: "up",
    },
    // 是否开启自动滚动
    autoPlay: {
      type: Boolean,
      default: true,
    },
    // 开启数据更新监听
    isWatch: {
      type: Boolean,
      default: true,
    },
    // 动画循环次数
    count: {
      type: [Number, String],
      default: "infinite",
      validator: function (value) {
        if (isNaN(value) && value !== "infinite") {
          console.warn("Must be in number format");
          return "infinite";
        }
        return value + "";
      },
    },
    // 动画时间
    duration: {
      type: [Number, String],
      validator,
    },
    // 贝塞尔曲线
    cubicBezier: {
      type: Object,
      default: {
        x1: 0,
        y1: 0,
        x2: 1,
        y2: 1,
      },
    },
  },
  setup(props, { slots }) {
    const scrollRef = ref(null);
    const slotListRef = ref(null);
    const realBoxRef = ref(null);
    const slotHtml = ref(slots.html != undefined);
    const scrollHtml = ref("");
    const cssPlayStatus = ref("paused");
    const realBoxWidth = ref(0);
    const realBoxHeight = ref(0);

    const isHorizontal = computed(
      () => props.direction == "left" || props.direction == "right"
    );

    const floatStyle = computed(() => {
      return isHorizontal.value
        ? { float: "left", overflow: "hidden" }
        : { overflow: "hidden" };
    });

    const isScroll = computed(() => props.datas.length >= props.limitScrollNum);

    const hoverStop = computed(
      () => props.hover && props.autoPlay && isScroll.value
    );

    const showHtml = computed(() => props.autoPlay && isScroll.value);

    function initMove() {
      nextTick(() => {
        dataWarm(props.datas);
        scrollHtml.value = "";
        if (isHorizontal.value) {
          let slotListWidth = slotListRef.value.offsetWidth;
          if (props.autoPlay) {
            slotListWidth = slotListWidth * 2 + 1;
          }
          realBoxWidth.value = slotListWidth;
        }

        if (props.autoPlay) {
          if (isScroll.value) {
            if (!slotHtml.value) {
              scrollHtml.value = slotListRef.value.innerHTML;
              nextTick(() => {
                realBoxHeight.value = realBoxRef.value.offsetHeight;
                cssPlayStatus.value = "running";
              });
            } else {
              realBoxHeight.value = realBoxRef.value.offsetHeight;
              cssPlayStatus.value = "running";
            }
          }
        }
      });
    }

    function enter() {
      if (hoverStop.value) {
        cssPlayStatus.value = "paused";
      }
    }

    function leave() {
      if (hoverStop.value) {
        cssPlayStatus.value = "running";
      }
    }

    watch(
      () => props.datas,
      () => {
        if (props.isWatch) {
          cssPlayStatus.value = "paused";
          initMove();
        }
      },
      {
        deep: true,
      }
    );

    watch(
      () => props.autoPlay,
      (newValue) => {
        if (newValue) {
          cssPlayStatus.value = "running";
        } else {
          cssPlayStatus.value = "paused";
        }
      }
    );

    onMounted(() => {
      initMove();
    });

    const cssDuration = computed(() => {
      if (props.duration !== undefined) {
        return `${props.duration}s`;
      }
      if (isHorizontal.value && realBoxWidth.value != 0) {
        return `${realBoxWidth.value / 2 / 60}s`;
      }
      if (realBoxHeight.value != 0) {
        return `${realBoxHeight.value / 2 / 60}s`;
      }
      return "0s";
    });

    const cssStep = computed(() => props.step);

    const cssCubicBezier = computed(() => {
      if (
        isNaN(props.cubicBezier.x1) ||
        isNaN(props.cubicBezier.y1) ||
        isNaN(props.cubicBezier.x1) ||
        isNaN(props.cubicBezier.y2)
      ) {
        console.warn("The Bézier curve value must be a number");
        return { x1: 0, y1: 0, x2: 1, y2: 1 };
      }
      return props.cubicBezier;
    });

    const cssCount = computed(() => {
      return props.count;
    });

    const cssRealBoxWidth = computed(() =>
      realBoxWidth.value != 0 ? `${realBoxWidth.value}px` : "auto"
    );

    const cssStartY = computed(() => {
      if (props.direction == "down") {
        return `${-realBoxHeight.value / 2}px`;
      }
      return "0";
    });

    const cssEndY = computed(() => {
      if (props.direction == "up") {
        return `${-realBoxHeight.value / 2}px`;
      }
      return "0";
    });

    const cssStartX = computed(() => {
      if (props.direction == "right") {
        return `${-realBoxWidth.value / 2}px`;
      }
      return "0";
    });

    const cssEndX = computed(() => {
      if (props.direction == "left") {
        return `${-realBoxWidth.value / 2}px`;
      }
      return "0";
    });

    return {
      scrollRef,
      slotListRef,
      realBoxRef,
      scrollHtml,
      floatStyle,
      slotHtml,
      showHtml,

      enter,
      leave,

      cssStep,
      cssDuration,
      cssCount,
      cssCubicBezier,
      cssRealBoxWidth,
      cssStartY,
      cssEndY,
      cssStartX,
      cssEndX,
      cssPlayStatus,
    };
  },
});
</script>

<style>
.realBox {
  width: v-bind(cssRealBoxWidth);
  overflow: hidden;
  animation-name: scroll_animation;
  animation-duration: v-bind(cssDuration);
  animation-iteration-count: v-bind(cssCount);
  animation-timing-function: cubic-bezier(
    v-bind("cssCubicBezier.x1"),
    v-bind("cssCubicBezier.y1"),
    v-bind("cssCubicBezier.x2"),
    v-bind("cssCubicBezier.y2")
  );
  animation-play-state: v-bind(cssPlayStatus);
  transform: translate(v-bind(cssStartX), v-bind(cssStartY));
}

.realBox.step {
  animation-timing-function: steps(v-bind(cssStep), start);
}

@keyframes scroll_animation {
  100% {
    z-index: 999;
    transform: translate(v-bind(cssEndX), v-bind(cssEndY));
  }
}
</style>
